import { useState, useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import axiosInstance from '../api/axiosInstance';
import { Message, User } from '../types';
import { API_CONFIG } from '../config';
import { getStoredToken } from '../utils/authStorage';
import { CameraFacingMode, getNextFacingMode, getReplacementVideoStreamAfterStoppingCurrent } from '../utils/camera';

interface FriendSnapshot {
  name?: string;
  isOnline?: boolean;
  lastSeen?: string;
}

interface PagedMessagesResponse {
  items: ProductionMessageResponse[];
  nextBeforeMessageId?: number | null;
  hasMore: boolean;
}

interface ProductionMessageResponse {
  messageId: number;
  conversationId: number;
  senderId: number;
  messageText?: string | null;
  mediaType: string;
  thumbnailUrl?: string | null;
  fullImageUrl?: string | null;
  voiceStreamUrl?: string | null;
  createdDate: string;
  isDeleted: boolean;
}

export const useChatSession = (
  currentUserId: string,
  otherUserId: string | undefined,
  conversationId?: number,
  friendSnapshot?: FriendSnapshot
) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [otherUser, setOtherUser] = useState<User | null>(null);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isOtherUserRecording, setIsOtherUserRecording] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPageVisibleRef = useRef<boolean>(true);
  // Always holds the latest loadMessages so SignalR handlers (created once) never use a stale closure
  const loadMessagesRef = useRef<(take?: number) => Promise<void>>(() => Promise.resolve());

  const loadMessages = useCallback(async (take: number = 15) => {
    if (!otherUserId) {
      setMessages([]);
      setHasMore(false);
      return;
    }

    try {
      const response = await axiosInstance.get<PagedMessagesResponse>(`/api/v2/friends/${otherUserId}/messages`, {
        params: { take }
      });
      const mapped = mapPagedMessages(response.data, parseInt(currentUserId), parseInt(otherUserId));
      setMessages(mapped);
      setHasMore(response.data.hasMore);
    } catch (error: any) {
      console.error('Failed to load messages:', error);
      // Don't show alert here to avoid spam - errors will be visible in console
      setMessages([]);
      setHasMore(false);
    }
  }, [currentUserId, otherUserId]);

  const loadOlderMessages = useCallback(async (oldestMessageId: number) => {
    if (!otherUserId) return [];
    try {
      if (!oldestMessageId || oldestMessageId <= 0) {
        console.error('Invalid oldestMessageId:', oldestMessageId);
        return [];
      }

      const response = await axiosInstance.get<PagedMessagesResponse>(`/api/v2/friends/${otherUserId}/messages/older`, {
        params: { beforeMessageId: oldestMessageId, take: 15 }
      });
      const mapped = mapPagedMessages(response.data, parseInt(currentUserId), parseInt(otherUserId));
      if (mapped.length > 0) {
        setMessages(prev => [...mapped, ...prev]);
        setHasMore(response.data.hasMore);
      } else {
        setHasMore(false);
      }
      return mapped;
    } catch (error: any) {
      console.error('Failed to load older messages:', error);
      setHasMore(false);
      return [];
    }
  }, [currentUserId, otherUserId]);

  const filterMessagesByDate = useCallback(async (date: string) => {
    if (!otherUserId) return [];
    try {
      if (!date || date.trim() === '') {
        console.error('Invalid date parameter:', date);
        return [];
      }

      const response = await axiosInstance.get<PagedMessagesResponse>(`/api/v2/friends/${otherUserId}/messages/by-date`, {
        params: { date }
      });
      const mapped = mapPagedMessages(response.data, parseInt(currentUserId), parseInt(otherUserId));
      setMessages(mapped);
      setHasMore(false);
      return mapped;
    } catch (error: any) {
      console.error('Failed to filter messages by date:', error);
      setMessages([]);
      return [];
    }
  }, [currentUserId, otherUserId]);

  // Keep the ref pointing at the latest loadMessages so handlers closed over the ref always work correctly
  loadMessagesRef.current = loadMessages;

  const loadOtherUser = useCallback(async () => {
    if (!otherUserId?.trim()) return;
    setOtherUser({
      id: parseInt(otherUserId),
      name: friendSnapshot?.name || 'Unknown User',
      isOnline: friendSnapshot?.isOnline || false,
      lastSeen: friendSnapshot?.lastSeen || ''
    });
  }, [friendSnapshot?.isOnline, friendSnapshot?.lastSeen, friendSnapshot?.name, otherUserId]);

  // Heartbeat function to keep connection alive and update last seen
  const startHeartbeat = useCallback((connection: signalR.HubConnection) => {
    // Clear any existing heartbeat interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    // Send heartbeat every 20 seconds (but only if page is visible)
    heartbeatIntervalRef.current = setInterval(() => {
      if (connection.state === signalR.HubConnectionState.Connected && isPageVisibleRef.current) {
        try {
          const userIdNum = parseInt(currentUserId);
          connection.invoke('Heartbeat', userIdNum).catch((err) => {
            console.error('Heartbeat failed:', err);
          });
        } catch (error) {
          console.error('Error sending heartbeat:', error);
        }
      }
    }, 20000); // 20 seconds
  }, [currentUserId]);

  const setupSignalR = useCallback(async () => {
    if (!otherUserId || connectionRef.current) return;

    const token = getStoredToken();
    const url = token
      ? `${API_CONFIG.chatHubURL}?access_token=${encodeURIComponent(token)}`
      : `${API_CONFIG.chatHubURL}?userId=${currentUserId}`;
    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        skipNegotiation: false,
        withCredentials: true
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // More aggressive reconnection: start with 0ms, then 1s, 2s, 5s, max 10s
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 1000;
          if (retryContext.previousRetryCount === 2) return 2000;
          if (retryContext.previousRetryCount === 3) return 5000;
          return 10000;
        }
      })
      .build();

    newConnection.on('MessageReceived', (message: ProductionMessageResponse) => {
      const currentUserIdNum = parseInt(currentUserId);
      const otherUserIdNum = parseInt(otherUserId);
      const mapped = mapProductionMessage(message, currentUserIdNum, otherUserIdNum);
      const isCurrentChatMessage =
        (conversationId && message.conversationId === conversationId) ||
        (mapped.fromUserId === currentUserIdNum && mapped.toUserId === otherUserIdNum) ||
        (mapped.fromUserId === otherUserIdNum && mapped.toUserId === currentUserIdNum);

      if (!isCurrentChatMessage) {
        return;
      }

      setMessages(prev => {
        const exists = prev.some(m => m.id === mapped.id);
        if (exists) {
          return prev;
        }
        return [...prev, mapped];
      });

      if (conversationId && mapped.fromUserId === otherUserIdNum) {
        axiosInstance.post(`/api/v2/conversations/${conversationId}/read`).catch((error) => {
          console.error('Failed to mark conversation as read:', error);
        });
      }
    });

    // Fallback: whenever FriendsChanged fires (sent by the server after every message),
    // reload messages from the API. This guarantees the recipient sees new messages even
    // if the MessageReceived event is missed (e.g., due to a brief connection glitch).
    newConnection.on('FriendsChanged', () => {
      loadMessagesRef.current();
    });

    newConnection.on('UserStatusChanged', (userId: string, isOnline: boolean, lastSeenUtc: string) => {
      console.log('UserStatusChanged received:', { userId, isOnline, lastSeenUtc, otherUserId });
      if (userId === otherUserId) {
        console.log('Updating other user status:', { userId, isOnline, lastSeenUtc });
        setOtherUser(prev => prev ? { ...prev, isOnline, lastSeen: lastSeenUtc } : null);
      }
    });

    newConnection.on('UserTyping', (userId: string, typing: boolean) => {
      if (userId === otherUserId) {
        setIsTyping(typing);
      }
    });

    newConnection.on('UserRecording', (userId: string, isRecording: boolean) => {
      if (userId === otherUserId) {
        setIsOtherUserRecording(isRecording);
      }
    });

    newConnection.on('ReactionAdded', (messageId: number, userId: number, emoji: string, reactionId: number) => {
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          const reactions = msg.reactions || [];
          const exists = reactions.some(r => r.id === reactionId);
          if (exists) {
            return msg;
          }
          return {
            ...msg,
            reactions: [...reactions, { id: reactionId, emoji, userId }]
          };
        }
        return msg;
      }));
    });

    newConnection.on('ReactionRemoved', (messageId: number, userId: number, emoji: string, reactionId?: number) => {
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          // If reactionId is provided, remove by ID, otherwise remove by userId and emoji
          const filtered = reactionId 
            ? (msg.reactions || []).filter(r => r.id !== reactionId)
            : (msg.reactions || []).filter(r => !(r.userId === userId && r.emoji === emoji));
          return {
            ...msg,
            reactions: filtered
          };
        }
        return msg;
      }));
    });

    newConnection.on('MessageDeleted', (messageId: number) => {
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
    });

    // Handle reconnection events
    newConnection.onreconnecting((error) => {
      console.log('SignalR reconnecting...', error);
    });

    newConnection.onreconnected(async (connectionId) => {
      console.log('SignalR reconnected with connectionId:', connectionId);
      
      // Re-register user when reconnected
      try {
        const userIdNum = parseInt(currentUserId);
        console.log(`Re-registering user after reconnect with userId: ${userIdNum}`);
        await newConnection.invoke('RegisterUser', userIdNum);
        if (conversationId) {
          await newConnection.invoke('JoinConversation', conversationId);
        }
        console.log('User re-registered successfully after reconnect');
        
        // Restart heartbeat after reconnection
        startHeartbeat(newConnection);
      } catch (registerError) {
        console.error('RegisterUser invocation failed on reconnect:', registerError);
      }
    });

    // Handle connection close (when reconnection fails or connection is explicitly closed)
    newConnection.onclose((error) => {
      console.log('SignalR connection closed', error);
      if (error) {
        console.error('Connection closed with error:', error);
        // Error 1006 indicates abnormal closure (network issues, server crash, etc.)
        // SignalR's automatic reconnect should handle this, but we log it for debugging
        if (error.message && error.message.includes('1006')) {
          console.warn('WebSocket error 1006 (abnormal closure) detected. Automatic reconnection will be attempted.');
        }
      }
      // Clear heartbeat interval when connection closes
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      // Don't clear the connection ref here - let automatic reconnect handle it
      // If reconnect fails, setupSignalR will be called again
    });

    // Helper function to register user
    const registerUser = async (connection: signalR.HubConnection) => {
      try {
        const userIdNum = parseInt(currentUserId);
        console.log(`Calling RegisterUser with userId: ${userIdNum}`);
        await connection.invoke('RegisterUser', userIdNum);
        if (conversationId) {
          await connection.invoke('JoinConversation', conversationId);
        }
        console.log('RegisterUser called successfully');
      } catch (registerError) {
        console.error('RegisterUser invocation failed:', registerError);
        throw registerError;
      }
    };

    try {
      await newConnection.start();
      console.log('SignalR WebSocket connected successfully');
      
      // Register user after initial connection
      await registerUser(newConnection);
      
      connectionRef.current = newConnection;
      setConnection(newConnection);
      
      // Start heartbeat interval (send heartbeat every 20 seconds)
      startHeartbeat(newConnection);
    } catch (error: any) {
      console.error('SignalR setup failed:', error);
      const errorMessage = error.message || 'Failed to connect to chat server. Please refresh the page.';
      alert(`Connection Error: ${errorMessage}`);
    }
  }, [conversationId, currentUserId, otherUserId, startHeartbeat]);

  useEffect(() => {
    if (!otherUserId) {
      setMessages([]);
      setOtherUser(null);
      setHasMore(false);
      return;
    }
    loadMessages();
    loadOtherUser();
    setupSignalR();
    
    // Handle page visibility changes (when user switches apps/tabs)
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = !document.hidden;
      
      if (!document.hidden && connectionRef.current) {
        // Page became visible - restart heartbeat and send immediate heartbeat
        if (connectionRef.current.state === signalR.HubConnectionState.Connected) {
          startHeartbeat(connectionRef.current);
          try {
            const userIdNum = parseInt(currentUserId);
            connectionRef.current.invoke('Heartbeat', userIdNum).catch((err) => {
              console.error('Immediate heartbeat failed:', err);
            });
          } catch (error) {
            console.error('Error sending immediate heartbeat:', error);
          }
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Handle beforeunload to properly disconnect
    const handleBeforeUnload = () => {
      if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
        // Try to stop the connection gracefully
        connectionRef.current.stop().catch((err) => {
          console.error('Error stopping connection on unload:', err);
        });
      }
      // Clear heartbeat interval
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Handle page unload (for mobile browsers that might not fire beforeunload)
    const handlePageHide = () => {
      if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
        // Use sendBeacon or similar if available, otherwise just stop
        connectionRef.current.stop().catch((err) => {
          console.error('Error stopping connection on page hide:', err);
        });
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
    
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      // Cleanup
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      
      if (connectionRef.current) {
        if (conversationId && connectionRef.current.state === signalR.HubConnectionState.Connected) {
          connectionRef.current.invoke('LeaveConversation', conversationId).catch((error) => {
            console.error('Failed to leave conversation group:', error);
          });
        }
        connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherUserId, startHeartbeat]);

  useEffect(() => {
    if (!otherUserId) return;

    const syncLatestMessages = () => {
      if (!document.hidden) {
        loadMessagesRef.current();
      }
    };

    const syncInterval = window.setInterval(syncLatestMessages, 3000);
    window.addEventListener('focus', syncLatestMessages);

    return () => {
      window.clearInterval(syncInterval);
      window.removeEventListener('focus', syncLatestMessages);
    };
  }, [otherUserId]);

  const sendMessage = async (_type: string, content: string, _replyToMessageId?: number): Promise<boolean> => {
    if (!content || !otherUserId) return false;

    const toUserIdNum = parseInt(otherUserId);
    if (isNaN(toUserIdNum)) {
      console.error('Invalid user IDs');
      return false;
    }

    try {
      await axiosInstance.post('/api/v2/messages/text', {
        recipientUserId: toUserIdNum,
        messageText: content,
      });
      await loadMessages();
      return true;
    } catch (error: any) {
      console.error('Failed to send message:', error);
      return false;
    }
  };

  const sendTyping = async (typing: boolean) => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.invoke('UserTyping', parseInt(currentUserId), typing);
      } catch (error) {
        console.error('Failed to send typing status:', error);
      }
    }
  };

  const sendRecordingStatus = async (isRecording: boolean) => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.invoke('UserRecording', parseInt(currentUserId), isRecording);
      } catch (error) {
        console.error('Failed to send recording status:', error);
      }
    }
  };

  const addReaction = async (messageId: number, emoji: string) => {
    if (connectionRef.current) {
      try {
        const userIdNum = parseInt(currentUserId);
        await connectionRef.current.invoke('AddReaction', messageId, userIdNum, emoji);
        console.log(`Reaction ${emoji} added to message ${messageId}`);
      } catch (error) {
        console.error('Failed to add reaction:', error);
      }
    }
  };

  const removeReaction = async (reactionId: number) => {
    if (connectionRef.current) {
      try {
        const userIdNum = parseInt(currentUserId);
        await connectionRef.current.invoke('RemoveReactionById', reactionId, userIdNum);
        console.log(`Reaction ${reactionId} removed`);
      } catch (error) {
        console.error('Failed to remove reaction:', error);
      }
    }
  };

  const deleteMessage = async (messageId: number) => {
    if (connectionRef.current) {
      try {
        const userIdNum = parseInt(currentUserId);
        await connectionRef.current.invoke('DeleteMessage', messageId, userIdNum);
        console.log(`Message ${messageId} deleted`);
      } catch (error) {
        console.error('Failed to delete message:', error);
      }
    }
  };

  // ===== WebRTC CALL SIGNALING CODE START =====
  // Add call state and peer connection management
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const [inCall, setInCall] = useState(false);
  const [inVideoCall, setInVideoCall] = useState(false);
  const [callType, setCallType] = useState<'audio' | 'video'>('audio');
  const callTypeRef = useRef<'audio' | 'video'>('audio');
  const [callStatus, setCallStatus] = useState<string | null>(null);
  const [isIncomingCall, setIsIncomingCall] = useState(false);
  const [callTimer, setCallTimer] = useState(0);
  const callTimerRef = useRef<NodeJS.Timeout | null>(null);
  const localAudioStreamRef = useRef<MediaStream | null>(null);
  const localVideoStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [cameraFacingMode, setCameraFacingMode] = useState<CameraFacingMode>('user');
  const cameraFacingModeRef = useRef<CameraFacingMode>('user');

  const updateCameraFacingMode = useCallback((nextFacingMode: CameraFacingMode) => {
    cameraFacingModeRef.current = nextFacingMode;
    setCameraFacingMode(nextFacingMode);
  }, []);

  const updateCallType = useCallback((nextCallType: 'audio' | 'video') => {
    callTypeRef.current = nextCallType;
    setCallType(nextCallType);
  }, []);

  function startCallTimer() {
    setCallTimer(0);
    if (callTimerRef.current) clearInterval(callTimerRef.current);
    callTimerRef.current = setInterval(() => setCallTimer(t => t+1), 1000);
  }

  function stopCallTimer() {
    if (callTimerRef.current) clearInterval(callTimerRef.current);
    setCallTimer(0);
  }

  // Setup a new PeerConnection
  async function setupPeerConnection() {
    if (peerConnectionRef.current) peerConnectionRef.current!.close();
    peerConnectionRef.current = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
    
    peerConnectionRef.current.onicecandidate = (event: any) => {
      if (event.candidate && connectionRef.current && otherUserId) {
        connectionRef.current!.invoke('IceCandidate', parseInt(currentUserId), parseInt(otherUserId), event.candidate);
      }
    };
    
    peerConnectionRef.current.ontrack = (event: any) => {
      const stream = event.streams[0];
      // Always try to set video first if available
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
      }
      // Also set audio for audio playback
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = stream;
      }
    };
  }

  function cleanupCall() {
    setInCall(false);
    setInVideoCall(false);
    setCallStatus(null);
    setIsIncomingCall(false);
    stopCallTimer();
    setIsMuted(false);
    setIsVideoOff(false);
    updateCameraFacingMode('user');
    
    if (peerConnectionRef.current) {
      peerConnectionRef.current!.close();
      peerConnectionRef.current = null;
    }
    
    if (localAudioStreamRef.current) {
      localAudioStreamRef.current.getTracks().forEach(track => track.stop());
      localAudioStreamRef.current = null;
    }
    
    if (localVideoStreamRef.current) {
      localVideoStreamRef.current.getTracks().forEach(track => track.stop());
      localVideoStreamRef.current = null;
    }
    
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
  }

  // Add signaling handlers
  const setupWebRTCSignaling = useCallback((signalRconnection: any) => {
    signalRconnection.off('CallOffer');
    signalRconnection.off('CallAnswer');
    signalRconnection.off('IceCandidate');
    signalRconnection.off('CallEnded');

    signalRconnection.on('CallOffer', async (fromUserId: string, toUserId: string, offer: any, callTypeParam?: 'audio' | 'video') => {
      // Only handle call offers meant for this user
      if (toUserId !== currentUserId) {
        return;
      }
      
      const callTypeReceived = callTypeParam || 'audio';
      updateCallType(callTypeReceived);
      setCallStatus('Incoming call...');
      setIsIncomingCall(true);
      await setupPeerConnection();
      const desc = new RTCSessionDescription(offer);
      await peerConnectionRef.current!.setRemoteDescription(desc);
      
      // Get media based on call type
      const constraints: MediaStreamConstraints = callTypeReceived === 'video' 
        ? { video: { facingMode: cameraFacingMode }, audio: true }
        : { audio: true };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (peerConnectionRef.current) stream.getTracks().forEach(track => peerConnectionRef.current!.addTrack(track, stream));
      
      if (callTypeReceived === 'video') {
        localVideoStreamRef.current = stream;
      } else {
        localAudioStreamRef.current = stream;
      }
      
      // Don't auto-answer - wait for user to accept/reject
    });

    signalRconnection.on('CallAnswer', async (fromUserId: string, toUserId: string, answer: any) => {
      // Only handle answers meant for this user
      if (toUserId !== currentUserId) {
        return;
      }
      
      if (peerConnectionRef.current) {
        await peerConnectionRef.current!.setRemoteDescription(new RTCSessionDescription(answer));
        if (callTypeRef.current === 'video') {
          setInVideoCall(true);
        } else {
          setInCall(true);
        }
        setCallStatus('In call');
        setIsIncomingCall(false);
        startCallTimer();
      }
    });
    
    signalRconnection.on('IceCandidate', async (fromUserId: string, toUserId: string, candidate: any) => {
      // Only handle ICE candidates meant for this user
      if (toUserId !== currentUserId) {
        return;
      }
      
      if (peerConnectionRef.current) {
        await peerConnectionRef.current!.addIceCandidate(new RTCIceCandidate(candidate));
      }
    });
    
    signalRconnection.on('CallEnded', async (fromUserId: string, toUserId: string) => {
      // Only handle end call if it's for this user
      if (toUserId !== currentUserId) {
        return;
      }
      cleanupCall();
    });

    return () => {
      signalRconnection.off('CallOffer');
      signalRconnection.off('CallAnswer');
      signalRconnection.off('IceCandidate');
      signalRconnection.off('CallEnded');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, updateCallType]);

  // Start audio call
  const startCall = async () => {
    if (!otherUserId) return;
    updateCallType('audio');
    setCallStatus('Calling...');
    setIsIncomingCall(false);
    await setupPeerConnection();
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (peerConnectionRef.current) stream.getTracks().forEach(track => peerConnectionRef.current!.addTrack(track, stream));
    localAudioStreamRef.current = stream;
    const offer = await peerConnectionRef.current!.createOffer();
    await peerConnectionRef.current!.setLocalDescription(offer);
    connectionRef.current!.invoke('CallOffer', parseInt(currentUserId), parseInt(otherUserId), offer, 'audio');
  };

  // Start video call
  const startVideoCall = async () => {
    if (!otherUserId) return;
    updateCallType('video');
    setCallStatus('Calling...');
    setIsIncomingCall(false);
    await setupPeerConnection();
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: cameraFacingMode }, audio: true });
    if (peerConnectionRef.current) stream.getTracks().forEach(track => peerConnectionRef.current!.addTrack(track, stream));
    localVideoStreamRef.current = stream;
    const offer = await peerConnectionRef.current!.createOffer();
    await peerConnectionRef.current!.setLocalDescription(offer);
    connectionRef.current!.invoke('CallOffer', parseInt(currentUserId), parseInt(otherUserId), offer, 'video');
  };

  // Accept incoming call
  const acceptCall = async () => {
    if (!peerConnectionRef.current || !otherUserId) return;
    
    // Create answer after remote description is set (from CallOffer handler)
    const answer = await peerConnectionRef.current!.createAnswer();
    await peerConnectionRef.current!.setLocalDescription(answer);
    connectionRef.current!.invoke('CallAnswer', parseInt(currentUserId), parseInt(otherUserId), answer);
    
    if (callTypeRef.current === 'video') {
      setInVideoCall(true);
    } else {
      setInCall(true);
    }
    setCallStatus('In call');
    startCallTimer();
  };

  // Reject incoming call
  const rejectCall = () => {
    cleanupCall();
    if (otherUserId && connectionRef.current) {
      connectionRef.current!.invoke('EndCall', parseInt(currentUserId), parseInt(otherUserId));
    }
  };

  // Toggle mute
  const toggleMute = () => {
    const stream = callType === 'video' ? localVideoStreamRef.current : localAudioStreamRef.current;
    if (stream) {
      stream.getAudioTracks().forEach(track => {
        track.enabled = isMuted;
      });
      setIsMuted(!isMuted);
    }
  };

  // Toggle video
  const toggleVideo = () => {
    if (localVideoStreamRef.current) {
      localVideoStreamRef.current.getVideoTracks().forEach(track => {
        track.enabled = isVideoOff;
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  // Switch front/rear camera without renegotiating or disconnecting the call
  const switchCamera = async () => {
    if (callTypeRef.current !== 'video' || !localVideoStreamRef.current || !peerConnectionRef.current) return;

    const currentVideoTrack = localVideoStreamRef.current.getVideoTracks()[0];
    const nextFacingMode = getNextFacingMode(cameraFacingModeRef.current);
    try {
      const replacementStream = await getReplacementVideoStreamAfterStoppingCurrent(nextFacingMode, currentVideoTrack);
      const replacementTrack = replacementStream.getVideoTracks()[0];
      if (!replacementTrack) return;

      replacementTrack.enabled = !isVideoOff;
      const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
      if (sender) {
        await sender.replaceTrack(replacementTrack);
      }

      const oldVideoTracks = localVideoStreamRef.current.getVideoTracks();
      oldVideoTracks.forEach(track => {
        localVideoStreamRef.current?.removeTrack(track);
        track.stop();
      });
      localVideoStreamRef.current.addTrack(replacementTrack);

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
        localVideoRef.current.srcObject = localVideoStreamRef.current;
        await localVideoRef.current.play().catch(() => undefined);
      }
      updateCameraFacingMode(nextFacingMode);
    } catch (error) {
      console.error('Failed to switch camera:', error);
    }
  };

  // Hang up/disconnect
  const endCall = () => {
    if (otherUserId && connectionRef.current) connectionRef.current!.invoke('EndCall', parseInt(currentUserId), parseInt(otherUserId));
    cleanupCall();
  };

  // Initialize SignalR handlers after connection is ready
  useEffect(() => {
    if (connection) {
      return setupWebRTCSignaling(connection);
    }
  }, [connection, setupWebRTCSignaling]);
  // ===== END WebRTC CALL SIGNALING =====

  // Make these available for ChatWindow
  return {
    messages,
    otherUser,
    connection,
    isTyping,
    isOtherUserRecording,
    hasMore,
    loadMessages,
    loadOlderMessages,
    filterMessagesByDate,
    sendMessage,
    sendTyping,
    sendRecordingStatus,
    addReaction,
    removeReaction,
    deleteMessage,
    otherUserId,
    startCall,
    startVideoCall,
    endCall,
    acceptCall,
    rejectCall,
    toggleMute,
    toggleVideo,
    switchCamera,
    inCall,
    inVideoCall,
    callType,
    callStatus,
    isIncomingCall,
    callTimer,
    remoteAudioRef,
    localVideoRef,
    remoteVideoRef,
    localVideoStreamRef,
    isMuted,
    isVideoOff,
    cameraFacingMode,
  };
};

function mapPagedMessages(data: PagedMessagesResponse, currentUserId: number, otherUserId: number): Message[] {
  if (!data?.items || !Array.isArray(data.items)) {
    return [];
  }

  return data.items.map((message) => mapProductionMessage(message, currentUserId, otherUserId));
}

function mapProductionMessage(message: ProductionMessageResponse, currentUserId: number, otherUserId: number): Message {
  return {
    id: message.messageId,
    fromUserId: message.senderId,
    toUserId: message.senderId === currentUserId ? otherUserId : currentUserId,
    type: message.mediaType,
    content: message.messageText || '',
    createdAtUtc: message.createdDate,
    createdAtIst: new Date(message.createdDate).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }),
    isRead: false,
    sentWhileOffline: false,
    imageDataUrl: message.fullImageUrl || message.thumbnailUrl || undefined,
    voiceDataUrl: message.voiceStreamUrl || undefined,
    reactions: [],
  };
}
